import { useState } from 'react';
import { X, Loader2, AlertCircle, CheckCircle, Car, ArrowLeft, ArrowRight } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useVertical } from '../../../../context/VerticalContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { VRMLookup, type FinalVehicleData } from '../../../../components/vehicles/VRMLookup';
import { refreshMotHistoryWithin } from '../../../../services/vrmService';
import { errorMessage } from '../../../../lib/errors';
import { vehicleColumnsFromLookup } from '../../../../lib/vehicle';

interface AddCustomerModalProps {
    onClose: () => void;
    onSuccess?: (newCustomerId: string) => void;
}

/**
 * Two steps, one modal, and nothing that leaves the page.
 *
 * This screen used to break the flow twice over, both times losing everything
 * the user had typed:
 *
 *  1. <VRMLookup> carries its own reg field, and this modal wrapped it in a
 *     <form>. A form inside a form is invalid HTML, and "LOOKUP" became a real
 *     GET submit — the Clients page reloaded as /clients?vrm=NA14VOP with the
 *     modal, and the half-typed customer, gone. VRMLookup no longer contains a
 *     form element at all; see the note in that file.
 *  2. On success it navigated to `/mechanic/clients/:id`, a route that does not
 *     exist. The catch-all `<Route path="*">` caught it and redirected to `/`,
 *     so creating a customer from the Clients page threw you out to the
 *     dashboard. The modal no longer navigates: it hands the new id back to
 *     whoever opened it and closes.
 *
 * The vehicle step is optional and comes second, because a reg is the one thing
 * the user may not have to hand — it must never gate creating the customer.
 */
type Step = 'details' | 'vehicle';

export default function AddCustomerModal({ onClose, onSuccess }: AddCustomerModalProps) {
    const [loading, setLoading] = useState(false);
    const { activeOrgId } = useVertical();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [step, setStep] = useState<Step>('details');

    // Reg entry goes through the shared <VRMLookup>, the same component the job,
    // quote, booking and vehicle-manager flows use. This screen used to carry its
    // own copy — a bespoke lookup button over a hand-rolled field grid, with no
    // local-database check and a different set of failure messages. Three
    // different VRM implementations existed across the app; this is one of the
    // two that were folded back in.
    const [vehicleConfirmed, setVehicleConfirmed] = useState(false);

    // A vehicle the lookup has found but the user has not tapped "Add This
    // Vehicle" on.
    //
    // On a phone that button sits inside the DVLA result card, below the fold,
    // while "Create Customer Profile" is pinned to the bottom of the screen.
    // Tapping the pinned one is the obvious move — and it used to create the
    // customer with no vehicle, silently, after the user had watched the app
    // find the make, model, MOT status and mileage. Nothing said it had been
    // thrown away; the vehicle was simply missing from the profile afterwards.
    //
    // So a resolved vehicle is adopted on submit rather than dropped.
    const [pendingVehicle, setPendingVehicle] = useState<FinalVehicleData | null>(null);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        // Vehicle Fields
        vrm: '',
        make: '',
        model: '',
        year: '',
        engine: '',
        mileage: '',
        colour: '',
        fuel_type: '',
        // Not shown as an input — DVLA knows it, the user has no reason to type
        // it, and send-mot-reminders needs it stored rather than admired.
        mot_expiry: '',
    });

    // Everything <VRMLookup> resolved — DVLA or an existing local record — lands
    // here. Keep every field: colour, fuel type and MOT expiry are what
    // send-mot-reminders and the vehicle card need, and dropping them just makes
    // the user type them again later.
    const handleVehicleConfirmed = (vehicle: FinalVehicleData) => {
        setFormData(prev => ({
            ...prev,
            vrm: vehicle.vrm || prev.vrm,
            make: vehicle.make || prev.make,
            model: vehicle.model || prev.model,
            year: String(vehicle.year || vehicle.yearOfManufacture || prev.year || ''),
            engine: vehicle.engine
                || (vehicle.engineCapacity ? String(vehicle.engineCapacity) : '')
                || prev.engine,
            mileage: vehicle.mileage ? String(vehicle.mileage) : prev.mileage,
            colour: vehicle.colour || prev.colour,
            fuel_type: vehicle.fuel_type || vehicle.fuelType || prev.fuel_type,
            mot_expiry: vehicle.motExpiryDate
                ? String(vehicle.motExpiryDate).slice(0, 10)
                : prev.mot_expiry,
        }));
        setVehicleConfirmed(true);
        setPendingVehicle(null);
    };

    // The reg lookup failed and the user chose to type it in. Carry the reg they
    // already entered across rather than making them retype it.
    const handleSkipToManual = (vrm: string) => {
        setFormData(prev => ({ ...prev, vrm: vrm.toUpperCase() }));
        setVehicleConfirmed(true);
    };

    // Derived, not an effect. Setting state from an effect body to say "the org
    // context is missing" is a cascading render for a message that is a pure
    // function of activeOrgId — and it also fought with the real errors below,
    // since whichever ran last won.
    const displayError = error ?? (activeOrgId ? null : 'Organisation Context Missing. Please refresh.');

    // Shared by "Next" and by creating without a vehicle, so the user is told
    // about a missing name on the step that holds the name field.
    const detailsAreValid = () => {
        if (!formData.full_name.trim()) {
            setError('Full Name is required.');
            return false;
        }
        setError(null);
        return true;
    };

    const goToVehicle = () => {
        if (!detailsAreValid()) return;
        setStep('vehicle');
    };

    /**
     * `withoutVehicle` is what the "Skip — create without a vehicle" button
     * means, and it has to be said explicitly: a user can look a reg up, go
     * Back to their details and then skip, and adopting the pending vehicle
     * there would attach a vehicle to a customer they just said had none.
     */
    const createCustomer = async ({ withoutVehicle = false } = {}) => {
        setError(null);

        if (!activeOrgId) {
            setError('Organisation ID is missing. Cannot create customer.');
            return;
        }

        if (!formData.full_name.trim()) {
            setStep('details');
            setError('Full Name is required.');
            return;
        }

        // Adopt a vehicle the lookup found but the user never confirmed. Their
        // intent is not in doubt — they looked the reg up on this screen — and
        // the alternative is losing it without a word.
        let vehicle = {
            vrm: formData.vrm.trim(),
            make: formData.make.trim(),
            model: formData.model.trim(),
            year: formData.year,
            engine: formData.engine.trim(),
            mileage: formData.mileage,
            colour: formData.colour.trim(),
            fuel_type: formData.fuel_type.trim(),
            mot_expiry: formData.mot_expiry,
        };

        if (!withoutVehicle && !vehicleConfirmed && pendingVehicle) {
            const cols = vehicleColumnsFromLookup(pendingVehicle);
            if (!cols.model) {
                // The one field DVLA never returns, so it is the one thing the
                // user still has to supply. Say so on the step that holds it.
                setStep('vehicle');
                setError('Vehicle Model is required — fill it in, or use "Change reg" to drop the vehicle.');
                return;
            }
            vehicle = {
                vrm: cols.vrm,
                make: cols.make ?? '',
                model: cols.model ?? '',
                year: cols.year != null ? String(cols.year) : '',
                engine: cols.engine ?? '',
                mileage: cols.mileage != null ? String(cols.mileage) : '',
                colour: cols.colour ?? '',
                fuel_type: cols.fuel_type ?? '',
                mot_expiry: cols.mot_expiry ?? '',
            };
        }

        setLoading(true);

        try {
            // Check for duplicate email within the active organization.
            //
            // TWO bugs fixed here, one of them live and unrelated to encryption:
            //
            //  1. This filtered on `customers.organization_id`. That column does
            //     not exist — it is `org_id`. PostgREST 400s on an unknown column
            //     and the next line THROWS that error, so adding a customer WITH
            //     an email address failed outright, while adding one without an
            //     email worked. Verified against the live schema on 2026-08-10.
            //
            //  2. `.eq('email', ...)` reads the plaintext column, which is now
            //     encrypted at rest and emptied by the purge script. The lookup
            //     goes through the keyed blind index instead, which also matches
            //     case-insensitively — the old filter was byte-exact, so
            //     "Bob@Example.com" did not match a stored "bob@example.com".
            if (formData.email.trim()) {
                const { data: existingId, error: emailError } = await supabase
                    .rpc('fn_find_my_customer_id_by_contact', {
                        p_org_id: activeOrgId,
                        p_email: formData.email.trim(),
                        p_phone: null,
                    });

                if (emailError) throw emailError;

                const { data: existingCustomer } = existingId
                    ? await supabase
                        .from('customers_decrypted')
                        .select('id, full_name')
                        .eq('id', existingId)
                        .maybeSingle()
                    : { data: null };

                if (existingCustomer) {
                    // The email lives on step one, so send the user back to it
                    // rather than showing a message about a field they cannot see.
                    setStep('details');
                    setError(`A customer with this email already exists: ${existingCustomer.full_name}. Please update the existing customer instead to prevent duplicates.`);
                    setLoading(false);
                    return;
                }
            }

            // Use the new Atomic RPC
            const { data, error } = await supabase
                .rpc('fn_create_customer_and_vehicle', {
                    p_org_id: activeOrgId,
                    p_full_name: formData.full_name.trim(),
                    p_phone: formData.phone.trim() || null,
                    p_email: formData.email.trim() || null,
                    p_address: formData.address.trim() || null,
                    // Vehicle (only passed if VRM is present)
                    p_vrm: vehicle.vrm || null,
                    p_make: vehicle.make || null,
                    p_model: vehicle.model || null,
                    p_year: vehicle.year ? parseInt(vehicle.year) : null,
                    p_engine: vehicle.engine || null,
                    p_mileage: vehicle.mileage ? parseInt(vehicle.mileage) : null,
                    p_colour: vehicle.colour || null,
                    p_fuel_type: vehicle.fuel_type || null,
                    p_mot_expiry: vehicle.mot_expiry || null
                });

            if (error) throw error;

            // Pull the vehicle's MOT history in behind the save.
            //
            // Bounded, not fire-and-forget: the user lands on this customer a
            // beat later, and an unfinished refresh is why the vehicle showed no
            // MOT date, no advisories and no mileage until they found the
            // Refresh button. The cap means a slow or unavailable DVSA delays
            // nothing — the customer and vehicle are already committed, and the
            // refresh completes in the background regardless.
            const newVehicleId = data?.vehicle_id;
            if (newVehicleId) await refreshMotHistoryWithin(newVehicleId);

            setSuccess(true);
            setTimeout(() => {
                const customerId = data?.customer_id; // typed as any/jsonb return

                if (onSuccess && customerId) {
                    // The opener decides what happens next. From the Clients page
                    // that is "stay put and confirm" — the user is usually adding
                    // more than one.
                    onSuccess(customerId);
                } else if (customerId) {
                    // Only for callers that pass no handler. `/clients/:id` is the
                    // real route; `/mechanic/clients/:id` was not, and silently
                    // redirected to the dashboard.
                    navigate(`/clients/${customerId}`);
                }
                onClose();
            }, 900);

        } catch (err) {
            console.error('Error creating customer:', err);
            setError(errorMessage(err) || 'Failed to create customer');
        } finally {
            setLoading(false);
        }
    };

    // Enter advances the flow rather than firing whatever button happens to be
    // first. The reg field inside <VRMLookup> stops its own Enter before it can
    // reach here.
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (step === 'details') goToVehicle();
        else void createCustomer();
    };

    const inputClass = 'w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-3 text-fg focus:ring-2 focus:ring-accent outline-none placeholder-fg-4 transition-all';
    const smallInputClass = 'w-full bg-tint/5 border border-tint/10 rounded-lg px-3 py-2.5 text-fg text-sm focus:ring-2 focus:ring-accent outline-none placeholder-fg-4';

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-md max-h-[92vh] flex flex-col bg-panel border border-line rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden"
            >
                <div className="p-6 pb-4 relative shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute top-3 right-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-fg-4 hover:text-fg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <h2 className="text-xl font-bold text-fg pr-12">Create New Customer</h2>
                    <p className="mt-1 text-xs font-bold uppercase tracking-wide text-fg-4">
                        {step === 'details' ? 'Step 1 of 2 — Their details' : 'Step 2 of 2 — Vehicle (optional)'}
                    </p>
                    <div className="mt-3 flex gap-1.5" aria-hidden="true">
                        <span className="h-1 flex-1 rounded-full bg-accent" />
                        <span className={`h-1 flex-1 rounded-full ${step === 'vehicle' ? 'bg-accent' : 'bg-tint/10'}`} />
                    </div>
                </div>

                {/* One <form>, and nothing inside it may be another one. The panel
                    scrolls internally so the action button stays reachable on a
                    phone with the keyboard up. */}
                <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
                    <div className="flex-1 min-h-0 overflow-y-auto px-6 space-y-4">

                        {step === 'details' && (
                            <>
                                <div>
                                    <label htmlFor="cust-name" className="block text-xs font-bold text-fg-4 uppercase mb-1">
                                        Full Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        id="cust-name"
                                        type="text"
                                        required
                                        value={formData.full_name}
                                        onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                                        className={inputClass}
                                        placeholder="ex. John Doe"
                                        autoFocus
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="min-w-0">
                                        <label htmlFor="cust-phone" className="block text-xs font-bold text-fg-4 uppercase mb-1">Phone</label>
                                        <input
                                            id="cust-phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                            className={`${inputClass} font-mono text-sm`}
                                            placeholder="07700 900000"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <label htmlFor="cust-email" className="block text-xs font-bold text-fg-4 uppercase mb-1">Email</label>
                                        <input
                                            id="cust-email"
                                            type="email"
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            className={`${inputClass} text-sm`}
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="cust-address" className="block text-xs font-bold text-fg-4 uppercase mb-1">Address</label>
                                    <textarea
                                        id="cust-address"
                                        value={formData.address}
                                        onChange={e => setFormData({ ...formData, address: e.target.value })}
                                        className={`${inputClass} h-24 resize-none text-sm`}
                                        placeholder="123 High Street&#10;London&#10;SW1A 1AA"
                                    />
                                </div>
                            </>
                        )}

                        {step === 'vehicle' && (
                            <>
                                {/* The same reg lookup as the job, quote and booking
                                    flows — checks this org's own vehicles first, then
                                    DVLA, and offers manual entry when neither answers. */}
                                {!vehicleConfirmed && (
                                    <>
                                        <p className="text-sm text-fg-3">
                                            Look up a registration and we'll fill in the rest. You can skip
                                            this and add a vehicle later.
                                        </p>
                                        <VRMLookup
                                            onVehicleConfirmed={handleVehicleConfirmed}
                                            onSkipToManual={handleSkipToManual}
                                            onPendingVehicleChange={setPendingVehicle}
                                            confirmButtonLabel="Add This Vehicle"
                                        />
                                        {pendingVehicle && (
                                            <p className="text-xs text-success/90">
                                                {pendingVehicle.vrm} will be added with this customer — tap
                                                &ldquo;Add This Vehicle&rdquo; to review it first, or just create
                                                the customer.
                                            </p>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => setVehicleConfirmed(true)}
                                            className="w-full min-h-[44px] text-xs font-bold text-fg-3 hover:text-fg underline underline-offset-2 transition-colors"
                                        >
                                            No reg? Enter the vehicle by hand
                                        </button>
                                    </>
                                )}

                                {vehicleConfirmed && (
                                    <>
                                        <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg bg-accent/10 border border-accent/20">
                                            <span className="flex items-center gap-2 min-w-0 font-mono text-lg tracking-wider text-fg break-all">
                                                <Car className="w-4 h-4 shrink-0 text-accent" />
                                                {formData.vrm || 'New vehicle'}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setVehicleConfirmed(false)}
                                                className="shrink-0 min-h-[44px] px-3 text-xs font-bold text-accent hover:underline"
                                            >
                                                Change reg
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                aria-label="Make"
                                                value={formData.make}
                                                onChange={e => setFormData({ ...formData, make: e.target.value })}
                                                className={smallInputClass}
                                                placeholder="Make (e.g. BMW)"
                                            />
                                            <input
                                                type="text"
                                                aria-label="Model"
                                                value={formData.model}
                                                onChange={e => setFormData({ ...formData, model: e.target.value })}
                                                className={smallInputClass}
                                                placeholder="Model"
                                            />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <input
                                                type="number"
                                                aria-label="Year"
                                                value={formData.year}
                                                onChange={e => setFormData({ ...formData, year: e.target.value })}
                                                className={`${smallInputClass} font-mono`}
                                                placeholder="Year"
                                            />
                                            <input
                                                type="text"
                                                aria-label="Engine"
                                                value={formData.engine}
                                                onChange={e => setFormData({ ...formData, engine: e.target.value })}
                                                className={smallInputClass}
                                                placeholder="Engine"
                                            />
                                            <input
                                                type="number"
                                                aria-label="Mileage"
                                                value={formData.mileage}
                                                onChange={e => setFormData({ ...formData, mileage: e.target.value })}
                                                className={`${smallInputClass} font-mono`}
                                                placeholder="Mileage"
                                            />
                                        </div>
                                        {/* Filled in by the lookup, editable by hand — a vehicle
                                            added manually can carry the same detail as one found
                                            on its reg, instead of arriving half-empty. */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="text"
                                                aria-label="Colour"
                                                value={formData.colour}
                                                onChange={e => setFormData({ ...formData, colour: e.target.value })}
                                                className={smallInputClass}
                                                placeholder="Colour"
                                            />
                                            <input
                                                type="text"
                                                aria-label="Fuel type"
                                                value={formData.fuel_type}
                                                onChange={e => setFormData({ ...formData, fuel_type: e.target.value })}
                                                className={smallInputClass}
                                                placeholder="Fuel type"
                                            />
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <AnimatePresence>
                            {displayError && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-start gap-2 text-danger text-sm"
                                >
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span className="min-w-0">{displayError}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <div className="shrink-0 p-6 pt-4 space-y-2 border-t border-tint/5 bg-panel">
                        {step === 'details' ? (
                            <>
                                <button
                                    type="submit"
                                    className="w-full font-bold py-3.5 min-h-[44px] rounded-lg transition-all flex items-center justify-center gap-2 fd-btn-primary"
                                >
                                    Next — add a vehicle
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { if (detailsAreValid()) void createCustomer({ withoutVehicle: true }); }}
                                    disabled={loading || success}
                                    className="w-full min-h-[44px] text-sm font-bold text-fg-3 hover:text-fg transition-colors disabled:opacity-50"
                                >
                                    Skip — create without a vehicle
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="submit"
                                    disabled={loading || success}
                                    className={`w-full font-bold py-3.5 min-h-[44px] rounded-lg transition-all flex items-center justify-center gap-2 ${success
                                        ? 'bg-success-fill text-on-fill'
                                        : 'fd-btn-primary'
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Creating...
                                        </>
                                    ) : success ? (
                                        <>
                                            <CheckCircle className="w-5 h-5" />
                                            Created!
                                        </>
                                    ) : (
                                        'Create Customer Profile'
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setError(null); setStep('details'); }}
                                    disabled={loading || success}
                                    className="w-full min-h-[44px] text-sm font-bold text-fg-3 hover:text-fg transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    Back to their details
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </motion.div>
        </div>
    );
}
